<ng-container *ngIf="state.state$ | async as draft">
    <header class="step-head">
        <div class="step-head__titles">
            <h2>Who works here?</h2>
            <p class="hint">
                Add the people who should land in this organization. We'll check each email — everyone you list
                gets an invitation email when you submit. Existing newgenus users appear on the org board
                immediately as <strong>Pending Acceptance</strong> on their assigned posts but have no rights
                or access until they accept and the assignment is approved; new emails get a
                "Create your account &amp; join" link and only appear once they sign up.
                You are added as the owner automatically and don't need a row here.
            </p>
        </div>
        <div class="step-head__meta">
            <span
                class="meta-chip"
                [class.meta-chip--warn]="draft.staff.length >= maxStaff"
                matTooltip="Maximum staff per draft (plan §16.4)"
            >
                <mat-icon>group</mat-icon>
                {{ draft.staff.length }} / {{ maxStaff }}
            </span>
        </div>
    </header>

    <!-- Phase B.2b validation banner (spec §6.5) — blocks Next while any row
         is still unresolved or invalid; gives the user a one-click recheck. -->
    <div class="resolution-gate" *ngIf="hasUnresolvedOrInvalid">
        <mat-icon>warning_amber</mat-icon>
        <span class="resolution-gate__msg">
            Resolve all email addresses before continuing —
            <ng-container *ngIf="unresolvedCount">
                {{ unresolvedCount }} pending check<ng-container *ngIf="unresolvedCount !== 1">s</ng-container>
            </ng-container>
            <ng-container *ngIf="unresolvedCount && invalidCount">, </ng-container>
            <ng-container *ngIf="invalidCount">
                {{ invalidCount }} invalid email<ng-container *ngIf="invalidCount !== 1">s</ng-container>
            </ng-container>.
        </span>
        <button
            *ngIf="unresolvedCount"
            mat-button
            color="primary"
            type="button"
            (click)="recheckUnresolvedRows()"
        >
            <mat-icon>refresh</mat-icon>
            Re-check now
        </button>
    </div>

    <div class="toolbar">
        <button
            mat-stroked-button
            color="primary"
            type="button"
            (click)="addRow()"
            [disabled]="draft.staff.length >= maxStaff"
        >
            <mat-icon>person_add</mat-icon>
            Add staff member
        </button>
        <button mat-button type="button" (click)="toggleBulkPaste()">
            <mat-icon>{{ showBulkPaste ? 'expand_less' : 'content_paste' }}</mat-icon>
            {{ showBulkPaste ? 'Hide bulk paste' : 'Bulk paste emails' }}
        </button>
        <button
            *ngIf="hasDuplicateEmails"
            mat-button
            color="warn"
            type="button"
            (click)="mergeDuplicateEmails()"
            matTooltip="Combine rows that share an email — their post assignments will be merged."
        >
            <mat-icon>merge_type</mat-icon>
            Merge duplicate emails
        </button>
    </div>

    <div class="bulk-paste" *ngIf="showBulkPaste">
        <mat-form-field appearance="outline" class="bulk-paste__field">
            <mat-label>Paste emails (one per line, optional "Name &lt;email&gt;")</mat-label>
            <textarea
                matInput
                rows="5"
                [(ngModel)]="bulkText"
                placeholder="jane@example.com&#10;John Smith <john@example.com>"
            ></textarea>
        </mat-form-field>
        <div class="bulk-paste__actions">
            <button mat-flat-button color="primary" type="button" (click)="commitBulkPaste()">Import</button>
            <span class="bulk-paste__feedback" *ngIf="bulkFeedback">{{ bulkFeedback }}</span>
        </div>
    </div>

    <ng-container *ngIf="draft.staff.length; else emptyState">
        <div class="table-wrap">
            <table class="staff-table">
                <thead>
                    <tr>
                        <th class="col-status">Status</th>
                        <th class="col-email">Email</th>
                        <th class="col-name">First name</th>
                        <th class="col-name">Last name</th>
                        <th class="col-post">Assign post</th>
                        <th class="col-chips">Posts assigned</th>
                        <th class="col-actions"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let row of draft.staff; trackBy: trackByRowId" [attr.data-row-id]="row.rowId">
                        <td class="col-status">
                            <span
                                class="status-chip status-chip--{{ row.resolutionStatus }}"
                                [matTooltip]="statusLabel(row.resolutionStatus)"
                            >
                                <mat-icon>{{ statusIcon(row.resolutionStatus) }}</mat-icon>
                            </span>
                        </td>

                        <td class="col-email">
                            <ng-container *ngIf="editingEmailRowId === row.rowId; else emailDisplay">
                                <mat-form-field appearance="outline" class="inline-field">
                                    <input
                                        matInput
                                        type="email"
                                        autocomplete="off"
                                        [(ngModel)]="editingEmailDraft"
                                        (keyup.enter)="commitEditEmail(row)"
                                        (keyup.escape)="cancelEditEmail()"
                                        (blur)="commitEditEmail(row)"
                                        cdkFocusInitial
                                        #emailInput
                                    />
                                </mat-form-field>
                            </ng-container>
                            <ng-template #emailDisplay>
                                <button
                                    type="button"
                                    class="email-display"
                                    [class.email-display--empty]="!row.email"
                                    (click)="startEditEmail(row)"
                                    (dblclick)="startEditEmail(row)"
                                >
                                    {{ row.email || 'Click to add email' }}
                                </button>
                            </ng-template>
                        </td>

                        <td class="col-name">
                            <mat-form-field appearance="outline" class="inline-field">
                                <input
                                    matInput
                                    [ngModel]="row.firstName"
                                    (ngModelChange)="updateFirstName(row, $event)"
                                    placeholder="First"
                                />
                            </mat-form-field>
                        </td>
                        <td class="col-name">
                            <mat-form-field appearance="outline" class="inline-field">
                                <input
                                    matInput
                                    [ngModel]="row.lastName"
                                    (ngModelChange)="updateLastName(row, $event)"
                                    placeholder="Last"
                                />
                            </mat-form-field>
                        </td>

                        <td class="col-post">
                            <shared-searchable-select
                                appearance="outline"
                                [data]="searchablePostsFrom(draft.structure)"
                                [control]="postControlFor(row)"
                                [multiple]="true"
                                [displayValue]="false"
                                [panelWidth]="360"
                                [compact]="true"
                                [label]="''"
                                [placeholder]="'Search posts…'"
                                [icon]="'account_tree'"
                                [iconColor]="'#1976d2'"
                                (selected)="onPostKeysChange(row, $event)"
                            ></shared-searchable-select>
                        </td>

                        <td class="col-chips">
                            <div class="post-chips" *ngIf="selectedPostsFor(row, draft.structure).length; else noPosts">
                                <span
                                    class="post-chip"
                                    *ngFor="let item of selectedPostsFor(row, draft.structure); trackBy: trackByValue"
                                >
                                    <mat-icon class="post-chip__icon">account_tree</mat-icon>
                                    <span class="post-chip__label">{{ item.display }}</span>
                                    <mat-icon
                                        class="post-chip__remove"
                                        matTooltip="Remove this post"
                                        (click)="removePostFromRow(row, item.value)"
                                    >close</mat-icon>
                                </span>
                            </div>
                            <ng-template #noPosts>
                                <span class="post-chips__empty">No posts yet</span>
                            </ng-template>
                        </td>

                        <td class="col-actions">
                            <button
                                mat-icon-button
                                color="warn"
                                type="button"
                                matTooltip="Remove staff row"
                                (click)="removeRow(row)"
                            >
                                <mat-icon>delete_outline</mat-icon>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </ng-container>

    <ng-template #emptyState>
        <div class="empty-state">
            <mat-icon>group_add</mat-icon>
            <h3>No staff added yet</h3>
            <p>
                You'll be the owner of this organization automatically. Add colleagues here so they get an
                invitation email the moment you click Create — they only gain access once they accept and the
                assignment is approved.
            </p>
            <button mat-flat-button color="primary" type="button" (click)="addRow()">
                <mat-icon>person_add</mat-icon>
                Add the first staff member
            </button>
        </div>
    </ng-template>
</ng-container>
